import { DocxPackage, DOCUMENT_PART } from './package/docx-package';
import { wrapBodyInField } from './ooxml/body-field';
import { FieldSet } from './ooxml/field-set';
import { flattenForPreview } from './ooxml/flatten-sdt';
import { parseXml, prologOf, serializeXml } from './ooxml/xml';
import { renderPreview, type FieldAnchor } from './render/preview';
import type { DocField, FieldValue } from './ooxml/field-kind';

export class DocTemplate {
  private readonly pkg: DocxPackage;
  private readonly part: Document;
  private readonly prolog: string;
  private readonly set: FieldSet;

  private constructor(pkg: DocxPackage, part: Document, prolog: string) {
    this.pkg = pkg;
    this.part = part;
    this.prolog = prolog;
    this.set = FieldSet.read(part);
  }

  static async open(file: Blob | ArrayBuffer | Uint8Array): Promise<DocTemplate> {
    const { pkg, part, prolog } = await DocTemplate.read(file);

    return new DocTemplate(pkg, part, prolog);
  }

  /**
   * Opens a document that has no content controls as one editable field, named
   * `label`. For documents we generate ourselves: without this the editor has
   * nothing to offer the reviewer.
   */
  static async openAsField(
    file: Blob | ArrayBuffer | Uint8Array,
    label: string,
  ): Promise<DocTemplate> {
    const { pkg, part, prolog } = await DocTemplate.read(file);

    wrapBodyInField(part, label);

    return new DocTemplate(pkg, part, prolog);
  }

  private static async read(
    file: Blob | ArrayBuffer | Uint8Array,
  ): Promise<{ pkg: DocxPackage; part: Document; prolog: string }> {
    const pkg = await DocxPackage.open(file);

    const source = pkg.text(DOCUMENT_PART);
    if (!source) {
      throw new Error('Not a Word document: word/document.xml is missing');
    }

    return { pkg, part: parseXml(source), prolog: prologOf(source) };
  }

  fields(): readonly DocField[] {
    return this.set.fields();
  }

  setValue(id: string, value: FieldValue): void {
    this.set.setValue(id, value);
  }

  async renderTo(body: HTMLElement, styles: HTMLElement): Promise<readonly FieldAnchor[]> {
    const preview = serializeXml(flattenForPreview(this.part), this.prolog);
    const ids = this.fields().map((f) => f.id);

    return renderPreview(this.pkg.toBlob({ [DOCUMENT_PART]: preview }), body, styles, ids);
  }

  toBlob(): Blob {
    return this.pkg.toBlob({ [DOCUMENT_PART]: serializeXml(this.part, this.prolog) });
  }
}
